import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { RotateCcw, Radio } from "lucide-react";
import { toast } from "sonner";

import { AdminAPI } from "@/lib/admin-api";
import { Card, CardBody } from "@/components/ui/Card";
import { Tabs } from "@/components/ui/Tabs";
import { Table, THead, TR, TH, TD } from "@/components/ui/Table";
import { Button } from "@/components/ui/Button";
import { StatusPill } from "@/components/ui/StatusPill";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { Modal } from "@/components/ui/Modal";
import { formatDateTime } from "@/lib/format";
import { truncateMiddle } from "@/lib/format";
import type { OutboxEvent } from "@/types/admin";

export function OutboxPage() {
  const [tab, setTab] = useState("pending");
  const [selected, setSelected] = useState<OutboxEvent | null>(null);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["admin-outbox", tab],
    queryFn: () => AdminAPI.listOutbox(tab === "all" ? undefined : tab),
    refetchInterval: tab === "pending" || tab === "dead_letter" ? 10_000 : false,
  });

  const retry = useMutation({
    mutationFn: (id: string) => AdminAPI.retryOutbox(id),
    onSuccess: () => {
      toast.success("Event requeued");
      qc.invalidateQueries({ queryKey: ["admin-outbox"] });
    },
  });

  const rows = data ?? [];

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-surface-900">Outbox / Events</h1>
        <p className="text-sm text-surface-500 mt-1">
          Transactional outbox events. Partition key preserves per-Entry ordering during dispatch.
        </p>
      </header>

      <Card>
        <div className="px-5 pt-3">
          <Tabs value={tab} onChange={setTab}
                tabs={[
                  { value: "pending",     label: "Pending"     },
                  { value: "dispatched",  label: "Dispatched"  },
                  { value: "dead_letter", label: "Dead Letter" },
                  { value: "all",         label: "All"         },
                ]} />
        </div>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="p-5 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12" />)}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState icon={Radio} title="No events in this view" />
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Event ID</TH><TH>Type</TH><TH>Partition</TH>
                  <TH>Attempts</TH><TH>Status</TH><TH>Occurred</TH><TH></TH>
                </TR>
              </THead>
              <tbody>
                {rows.map((e) => (
                  <TR key={e.event_id} onClick={() => setSelected(e)}>
                    <TD className="font-mono text-2xs">{truncateMiddle(e.event_id, 8)}</TD>
                    <TD className="font-mono text-xs">{e.event_type}</TD>
                    <TD className="font-mono text-2xs">{e.partition_key}</TD>
                    <TD>{e.attempts}</TD>
                    <TD><StatusPill status={e.status} /></TD>
                    <TD className="text-2xs text-surface-500">{formatDateTime(e.occurred_at)}</TD>
                    <TD>
                      {e.status === "dead_letter" && (
                        <Button size="sm" variant="outline"
                                leftIcon={<RotateCcw className="h-3.5 w-3.5" />}
                                loading={retry.isPending}
                                onClick={(evt) => { evt.stopPropagation(); retry.mutate(e.event_id); }}>
                          Retry
                        </Button>
                      )}
                    </TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      {selected && (
        <Modal open onClose={() => setSelected(null)}
               title={`Event ${truncateMiddle(selected.event_id, 8)}`} size="lg">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <Field label="Type" value={<span className="font-mono">{selected.event_type}</span>} />
              <Field label="Partition" value={<span className="font-mono">{selected.partition_key}</span>} />
              <Field label="Status" value={<StatusPill status={selected.status} />} />
              <Field label="Attempts" value={selected.attempts} />
              <Field label="Correlation ID" value={<span className="font-mono text-xs">{selected.correlation_id}</span>} />
              <Field label="Occurred" value={formatDateTime(selected.occurred_at)} />
            </div>
            {selected.last_error && (
              <div>
                <div className="text-2xs font-semibold uppercase text-surface-500 mb-1">Last error</div>
                <pre className="text-xs bg-danger-50 border border-danger-200 rounded-lg p-3 whitespace-pre-wrap font-mono">
                  {selected.last_error}
                </pre>
              </div>
            )}
            <div>
              <div className="text-2xs font-semibold uppercase text-surface-500 mb-1">Payload</div>
              <pre className="text-xs bg-surface-50 border border-surface-200 rounded-lg p-3 overflow-auto max-h-96 font-mono">
                {JSON.stringify(selected.payload, null, 2)}
              </pre>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-2xs font-semibold uppercase text-surface-500">{label}</div>
      <div className="mt-0.5 text-surface-900">{value}</div>
    </div>
  );
}
